import {useEffect, useState, useRef} from "react";
import {useSearchParams, Link, useNavigate} from "react-router-dom";
import {getFromApi} from "../api/auth";

const SECTION_META = {
    notes:     {label: "Notes",       icon: "bi-journal-text",   color: "#6366f1"},
    bookmarks: {label: "Bookmarks",   icon: "bi-bookmark",       color: "#3b82f6"},
    todo:      {label: "Todo",        icon: "bi-check2-square",  color: "#10b981"},
    projects:  {label: "Project Log", icon: "bi-kanban",         color: "#f59e0b"},
    storage:   {label: "Storage",     icon: "bi-folder",         color: "#8b5cf6"},
};

const TYPE_ICON = {
    "note-folder":    "bi-journal-text",
    "note":           "bi-file-text",
    "bookmark":       "bi-bookmark-fill",
    "todo-group":     "bi-list-check",
    "todo-item":      "bi-check2-square",
    "project":        "bi-folder2",
    "log":            "bi-card-text",
    "storage-folder": "bi-folder-fill",
    "storage-file":   "bi-file-earmark",
};

function ResultItem({item}) {
    const icon = TYPE_ICON[item.type] || "bi-circle";

    if (item.external) return (
        <a href={item.url} target="_blank" rel="noopener noreferrer"
           className="d-flex align-items-center gap-3 px-3 py-2 border-bottom text-decoration-none text-dark">
            <i className={`bi ${icon} text-muted flex-shrink-0`}/>
            <div className="min-w-0">
                <div className="fw-medium small text-truncate">{item.label}</div>
                <div className="text-muted" style={{fontSize:"0.7rem"}}>{item.sub}</div>
            </div>
            <i className="bi bi-box-arrow-up-right text-muted ms-auto flex-shrink-0" style={{fontSize:"0.75rem"}}/>
        </a>
    );

    return (
        <Link to={item.url}
              className="d-flex align-items-center gap-3 px-3 py-2 border-bottom text-decoration-none text-dark">
            <i className={`bi ${icon} text-muted flex-shrink-0`}/>
            <div className="min-w-0">
                <div className="fw-medium small text-truncate">{item.label}</div>
                <div className="text-muted" style={{fontSize:"0.7rem"}}>{item.sub}</div>
            </div>
            <i className="bi bi-chevron-right text-muted ms-auto flex-shrink-0" style={{fontSize:"0.75rem"}}/>
        </Link>
    );
}

function Section({sectionKey, items}) {
    const meta = SECTION_META[sectionKey];
    if (!items?.length) return null;
    return (
        <div className="panel p-0 mb-4" style={{overflow:"hidden"}}>
            <div className="px-3 py-2 border-bottom d-flex align-items-center gap-2" style={{background:"#f9fafb"}}>
                <i className={`bi ${meta.icon}`} style={{color: meta.color}}/>
                <span className="fw-semibold small">{meta.label}</span>
                <span className="badge text-bg-secondary ms-auto">{items.length}</span>
            </div>
            {items.map(item => <ResultItem key={String(item.id)} item={item}/>)}
        </div>
    );
}

export default function Search() {
    const [searchParams] = useSearchParams();
    const navigate       = useNavigate();
    const q              = searchParams.get("q") || "";
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [input, setInput]     = useState(q);
    const inputRef = useRef();

    useEffect(() => {
        setInput(q);
        if (q.length < 2) { setResults(null); return; }
        setLoading(true);
        getFromApi(`api/search?q=${encodeURIComponent(q)}`)
            .then(res => setResults(res.data || {}))
            .catch(() => setResults({}))
            .finally(() => setLoading(false));
    }, [q]);

    const handleSubmit = (e) => {
        e.preventDefault();
        const term = input.trim();
        if (term.length < 2) return;
        navigate(`/search?q=${encodeURIComponent(term)}`);
    };

    const totalHits = results
        ? Object.values(results).reduce((sum, arr: any) => sum + (arr?.length || 0), 0)
        : 0;

    return (
        <div>
            <div className="page-header">
                <h4><i className="bi bi-search me-2 text-muted"/>Search</h4>
            </div>

            <form onSubmit={handleSubmit} className="mb-4">
                <div className="input-group">
                    <span className="input-group-text"><i className="bi bi-search"/></span>
                    <input
                        ref={inputRef}
                        autoFocus
                        className="form-control"
                        placeholder="Search notes, bookmarks, todos, projects, storage…"
                        value={input}
                        onChange={e => setInput(e.target.value)}
                    />
                    <button type="submit" className="btn btn-dark">Search</button>
                </div>
            </form>

            {loading && (
                <div className="text-muted text-center py-5">
                    <div className="spinner-border spinner-border-sm me-2"/>Searching…
                </div>
            )}

            {results && !loading && (
                <>
                    <div className="text-muted small mb-3">
                        {totalHits === 0
                            ? `No results for "${q}"`
                            : `${totalHits} result${totalHits !== 1 ? "s" : ""} for "${q}"`}
                    </div>
                    {Object.entries(results).map(([key, items]) => (
                        <Section key={key} sectionKey={key} items={items}/>
                    ))}
                </>
            )}

            {!q && !loading && (
                <div className="empty-state">
                    <i className="bi bi-search"/>
                    <div>Type at least 2 characters to search across all sections.</div>
                </div>
            )}
        </div>
    );
}
